import {
  USERNAME_MAX,
  getHeroDef,
  isHeroId,
  passwordError,
  usernameError,
  type DirectMessage,
  type FriendEntry,
  type HeroId,
  type SocialInvite,
  type SocialState,
} from '@moba/shared';
import type { AccountClient } from './Account.ts';
import type { SocialClient } from './SocialClient.ts';

import { t, tr, presenceLabelT, modeCodeLabel } from './i18n.ts';
/**
 * Interfaz de cuentas y amigos:
 * - recuadro de cuenta en el menú (invitado / iniciar sesión / usuario + AMIGOS),
 * - ventana de inicio de sesión y registro,
 * - panel de amigos (F o el botón AMIGOS): agregar por nombre, solicitudes,
 *   lista con estado en vivo y acciones Unirse / Invitar / Mensaje / Eliminar,
 *   y conversación privada con cada amigo,
 * - avisos: invitaciones (Unirse / Ignorar), mensajes nuevos y notificaciones.
 * Todo el texto de usuarios se pinta con `textContent` (nunca HTML).
 */

export interface SocialUiDeps {
  account: AccountClient;
  social: SocialClient;
  /** sala online en la que estoy (para invitar), o null */
  currentRoom: () => { roomId: string; mode: string } | null;
  /** entrar en una sala por código; devuelve un error legible si ahora no se puede */
  joinRoom: (roomId: string) => string | null;
}

const DM_KEEP = 100;

function h<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text = ''): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text) element.textContent = text;
  return element;
}

function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const element = h('button', className, label);
  element.type = 'button';
  element.addEventListener('click', onClick);
  return element;
}

/** Los campos de texto no deben disparar los atajos del juego (Q, P, M…). */
function isolateKeys(input: HTMLElement, onEscape?: () => void): void {
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && onEscape) onEscape();
    event.stopPropagation();
  });
}

const heroName = (id: string): string => (isHeroId(id) ? getHeroDef(id).displayName : '');

export class SocialUi {
  private readonly widget: HTMLElement;
  private readonly hudButton: HTMLButtonElement;
  private readonly panel: HTMLElement;
  private readonly panelBody: HTMLElement;
  private readonly panelSub: HTMLElement;
  private readonly modal: HTMLElement;
  private readonly toasts: HTMLElement;
  private readonly dms = new Map<number, DirectMessage[]>();
  private readonly unread = new Map<number, number>();
  private chatWith: number | null = null;
  private authMode: 'login' | 'register' = 'login';
  private connected = false;

  constructor(private readonly deps: SocialUiDeps) {
    // recuadro de cuenta (pantalla de inicio)
    this.widget = h('div');
    this.widget.id = 'account-widget';
    document.getElementById('overlay')?.appendChild(this.widget);

    // botón AMIGOS en el panel del jugador (en partida)
    this.hudButton = button(t('AMIGOS'), '', () => this.togglePanel());
    this.hudButton.id = 'friends-hud-btn';
    this.hudButton.title = t('Amigos (F)');
    this.hudButton.hidden = true;
    document.querySelector('#player-panel .player-stats')?.appendChild(this.hudButton);

    // panel de amigos
    this.panel = h('aside');
    this.panel.id = 'friends-panel';
    this.panel.hidden = true;
    const head = h('header', 'friends-head');
    const titles = h('div');
    titles.append(h('div', 'kicker', t('AMIGOS')));
    this.panelSub = h('div', 'friends-sub');
    titles.append(this.panelSub);
    head.append(titles, button('×', 'friends-close', () => this.closePanel()));
    this.panelBody = h('div', 'friends-body');
    this.panel.append(head, this.panelBody);
    document.body.appendChild(this.panel);

    // inicio de sesión / registro
    this.modal = h('div');
    this.modal.id = 'auth-modal';
    this.modal.className = 'modal';
    this.modal.hidden = true;
    document.body.appendChild(this.modal);

    this.toasts = h('div');
    this.toasts.id = 'social-toasts';
    document.body.appendChild(this.toasts);

    window.addEventListener('keydown', (event) => {
      if (event.code !== 'KeyF' || event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (!this.deps.account.loggedIn) return;
      this.togglePanel();
    });

    deps.account.onChange(() => this.onAccountChange());
    this.onAccountChange();
  }

  // ------------------------------------------------------------------ eventos del servidor

  setConnected(connected: boolean): void {
    this.connected = connected;
    this.renderWidget();
    this.renderPanel();
  }

  onState(): void {
    this.renderWidget();
    this.renderPanel();
  }

  onInvite(invite: SocialInvite): void {
    const mode = modeCodeLabel(invite.mode);
    const toast = this.toast('invite', t('{name} te invita a su sala', { name: invite.from.username }) + (mode ? ` (${mode})` : ''), 20000);
    const actions = h('div', 'toast-actions');
    actions.append(
      button(t('Unirse'), 'toast-btn primary', () => {
        toast.remove();
        this.join(invite.roomId);
      }),
      button(t('Ignorar'), 'toast-btn', () => toast.remove()),
    );
    toast.append(actions);
  }

  onMessage(message: DirectMessage): void {
    const me = this.deps.social.state?.me.id ?? this.deps.account.user?.id;
    const friendId = message.from.id === me ? message.toId : message.from.id;
    const log = this.dms.get(friendId) ?? [];
    log.push(message);
    if (log.length > DM_KEEP) log.splice(0, log.length - DM_KEEP);
    this.dms.set(friendId, log);
    const viewing = !this.panel.hidden && this.chatWith === friendId;
    if (message.from.id !== me && !viewing) {
      this.unread.set(friendId, (this.unread.get(friendId) ?? 0) + 1);
      const toast = this.toast('dm', `${message.from.username}: ${message.text}`, 6000);
      toast.classList.add('clickable');
      toast.addEventListener('click', () => {
        toast.remove();
        this.openChat(friendId);
      });
    }
    this.renderWidget();
    if (!this.panel.hidden) this.renderPanel();
  }

  notice(message: string): void {
    this.toast('notice', message, 3500);
  }

  error(message: string): void {
    this.toast('error', message, 4500);
  }

  // ------------------------------------------------------------------ cuenta

  private onAccountChange(): void {
    const loggedIn = this.deps.account.loggedIn;
    this.hudButton.hidden = !loggedIn;
    if (!loggedIn) {
      this.closePanel();
      this.dms.clear();
      this.unread.clear();
    }
    this.renderWidget();
  }

  private renderWidget(): void {
    const { account, social } = this.deps;
    this.widget.replaceChildren();
    if (!account.loggedIn) {
      this.widget.append(h('span', 'account-guest', t('Jugando como invitado')));
      this.widget.append(button(t('INICIAR SESIÓN'), 'account-btn primary', () => this.openAuth('login')));
      this.widget.append(button(t('CREAR CUENTA'), 'account-btn', () => this.openAuth('register')));
      return;
    }
    const state = social.state;
    const dot = h('span', `presence-dot ${this.connected ? 'online' : 'connecting'}`);
    dot.title = this.connected ? t('En línea') : t('Conectando…');
    const name = h('span', 'account-name', account.user!.username);
    const online = state ? state.friends.filter((f) => f.online).length : 0;
    const friends = button(state ? t('AMIGOS · {count} en línea', { count: online }) : t('AMIGOS'), 'account-btn primary', () => this.togglePanel());
    const pending = (state?.incoming.length ?? 0) + [...this.unread.values()].reduce((a, b) => a + b, 0);
    if (pending > 0) friends.append(h('span', 'badge', String(pending)));
    const logout = button(t('Salir'), 'account-btn link', () => {
      if (window.confirm(t('¿Cerrar sesión? Seguirás jugando como invitado.'))) void account.logout();
    });
    this.widget.append(dot, name, friends, logout);
    // el botón del HUD muestra lo pendiente
    this.hudButton.textContent = t('AMIGOS');
    if (pending > 0) this.hudButton.append(h('span', 'badge', String(pending)));
  }

  openAuth(mode: 'login' | 'register'): void {
    this.authMode = mode;
    this.renderAuth();
    this.modal.hidden = false;
    (this.modal.querySelector('input') as HTMLInputElement | null)?.focus();
  }

  private closeAuth(): void {
    this.modal.hidden = true;
  }

  private renderAuth(): void {
    const register = this.authMode === 'register';
    const card = h('form', 'modal-card modal-narrow auth-card');
    card.noValidate = true;
    const head = h('header', 'modal-head');
    const titles = h('div', 'modal-titles');
    titles.append(h('div', 'kicker', t('CUENTA')), h('h2', '', register ? t('Crear cuenta') : t('Iniciar sesión')));
    const close = button('×', 'modal-close', () => this.closeAuth());
    close.setAttribute('aria-label', t('Cerrar'));
    head.append(titles, close);
    const body = h('div', 'modal-body');
    const foot = h('footer', 'modal-foot auth-foot');
    card.append(head, body, foot);
    const tabs = h('div', 'auth-tabs');
    const tab = (mode: 'login' | 'register', label: string) => {
      const element = button(label, `auth-tab${this.authMode === mode ? ' active' : ''}`, () => {
        this.authMode = mode;
        this.renderAuth();
        (this.modal.querySelector('input') as HTMLInputElement | null)?.focus();
      });
      return element;
    };
    tabs.append(tab('login', t('Iniciar sesión')), tab('register', t('Crear cuenta')));
    const field = (label: string, type: string, autocomplete: string) => {
      const wrap = h('label', 'field');
      wrap.append(h('span', '', label));
      const input = h('input');
      input.type = type;
      input.setAttribute('autocomplete', autocomplete);
      input.spellcheck = false;
      if (type === 'text') input.maxLength = USERNAME_MAX;
      isolateKeys(input, () => this.closeAuth());
      wrap.append(input);
      body.append(wrap);
      return input;
    };
    body.append(tabs);
    const username = field(t('Nombre de usuario'), 'text', 'username');
    const password = field(t('Contraseña'), 'password', register ? 'new-password' : 'current-password');
    const repeat = register ? field(t('Repite la contraseña'), 'password', 'new-password') : null;
    if (register) body.append(h('p', 'auth-hint', t('3 a 16 letras, números o _. Será tu nombre en las partidas y el que usarán tus amigos para agregarte.')));
    const error = h('div', 'auth-error');
    const submit = h('button', 'btn btn-primary btn-block', register ? t('Crear cuenta') : t('Entrar'));
    submit.type = 'submit';
    const guest = button(t('Seguir como invitado'), 'link-btn', () => this.closeAuth());
    body.append(error);
    foot.append(submit, guest);
    card.addEventListener('submit', (event) => {
      event.preventDefault();
      const name = username.value.trim();
      const problem = register
        ? usernameError(name) ?? passwordError(password.value) ?? (repeat && repeat.value !== password.value ? t('Las contraseñas no coinciden') : null)
        : !name || !password.value
          ? t('Escribe tu usuario y tu contraseña')
          : null;
      if (problem) {
        error.textContent = tr(problem);
        return;
      }
      submit.disabled = true;
      error.textContent = '';
      const action = register ? this.deps.account.register(name, password.value) : this.deps.account.login(name, password.value);
      action
        .then(() => {
          this.closeAuth();
          this.notice(register ? t('¡Cuenta creada! Bienvenido, {name}', { name }) : t('¡Hola de nuevo, {name}!', { name: this.deps.account.user?.username ?? name }));
        })
        .catch((reason: unknown) => {
          error.textContent = reason instanceof Error ? reason.message : t('No se pudo completar');
          submit.disabled = false;
        });
    });
    this.modal.replaceChildren(card);
    this.modal.onclick = (event) => {
      if (event.target === this.modal) this.closeAuth();
    };
  }

  // ------------------------------------------------------------------ panel de amigos

  togglePanel(): void {
    if (this.panel.hidden) this.openPanel();
    else this.closePanel();
  }

  openPanel(): void {
    if (!this.deps.account.loggedIn) {
      this.openAuth('login');
      return;
    }
    this.panel.hidden = false;
    this.renderPanel();
  }

  closePanel(): void {
    this.panel.hidden = true;
    this.chatWith = null;
  }

  private openChat(friendId: number): void {
    this.chatWith = friendId;
    this.unread.delete(friendId);
    this.panel.hidden = false;
    this.renderPanel();
    this.renderWidget();
    (this.panel.querySelector('.dm-form input') as HTMLInputElement | null)?.focus();
  }

  private join(roomId: string): void {
    const problem = this.deps.joinRoom(roomId);
    if (problem) this.error(problem);
    else this.closePanel();
  }

  private renderPanel(): void {
    if (this.panel.hidden) return;
    const state = this.deps.social.state;
    if (!state) {
      this.panelSub.textContent = this.deps.account.user?.username ?? '';
      this.panelBody.replaceChildren(h('p', 'friends-empty', this.connected ? t('Cargando…') : t('Conectando con el servidor de amigos…')));
      return;
    }
    const online = state.friends.filter((f) => f.online).length;
    this.panelSub.textContent = t('{name} · {online} de {total} en línea', { name: state.me.username, online, total: state.friends.length });
    const friend = this.chatWith !== null ? state.friends.find((f) => f.id === this.chatWith) : undefined;
    if (friend) this.renderChat(friend);
    else {
      this.chatWith = null;
      this.renderList(state);
    }
  }

  private renderList(state: SocialState): void {
    const { social } = this.deps;
    const body: HTMLElement[] = [];

    const form = h('form', 'friend-add');
    const input = h('input');
    input.placeholder = t('Nombre de usuario');
    input.maxLength = USERNAME_MAX;
    input.autocomplete = 'off';
    input.spellcheck = false;
    isolateKeys(input, () => this.closePanel());
    const add = h('button', 'friends-btn primary', t('AGREGAR'));
    add.type = 'submit';
    form.append(input, add);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const name = input.value.trim();
      if (!name) return;
      social.send({ type: 'request', username: name });
      input.value = '';
    });
    body.push(form);

    if (state.incoming.length) {
      const section = h('section', 'friends-section');
      section.append(h('div', 'friends-label', t('SOLICITUDES ({count})', { count: state.incoming.length })));
      for (const user of state.incoming) {
        const row = h('div', 'friend-row request');
        const info = h('div', 'friend-info');
        info.append(h('span', 'friend-name', user.username), h('span', 'friend-status', t('Quiere ser tu amigo')));
        const actions = h('div', 'friend-actions');
        actions.append(
          button(t('Aceptar'), 'friends-btn primary', () => social.send({ type: 'respond', userId: user.id, accept: true })),
          button(t('Rechazar'), 'friends-btn', () => social.send({ type: 'respond', userId: user.id, accept: false })),
        );
        row.append(info, actions);
        section.append(row);
      }
      body.push(section);
    }

    const section = h('section', 'friends-section');
    section.append(h('div', 'friends-label', t('AMIGOS')));
    if (!state.friends.length) {
      section.append(h('p', 'friends-empty', t('Aún no tienes amigos. Agrega a alguien con su nombre de usuario.')));
    }
    const myRoom = this.deps.currentRoom();
    for (const friend of state.friends) section.append(this.friendRow(friend, myRoom));
    body.push(section);

    if (state.outgoing.length) {
      const sent = h('section', 'friends-section');
      sent.append(h('div', 'friends-label', t('ENVIADAS')));
      for (const user of state.outgoing) {
        const row = h('div', 'friend-row');
        const info = h('div', 'friend-info');
        info.append(h('span', 'friend-name', user.username), h('span', 'friend-status', t('Solicitud pendiente')));
        const actions = h('div', 'friend-actions');
        actions.append(button(t('Cancelar'), 'friends-btn', () => social.send({ type: 'cancel', userId: user.id })));
        row.append(info, actions);
        sent.append(row);
      }
      body.push(sent);
    }
    this.panelBody.replaceChildren(...body);
  }

  private friendRow(friend: FriendEntry, myRoom: { roomId: string; mode: string } | null): HTMLElement {
    const { social } = this.deps;
    const row = h('div', `friend-row${friend.online ? ' online' : ''}`);
    const dot = h('span', `presence-dot ${friend.online ? (friend.status?.state === 'match' || friend.status?.state === 'offline-match' ? 'busy' : 'online') : 'offline'}`);
    const info = h('div', 'friend-info');
    info.append(h('span', 'friend-name', friend.username), h('span', 'friend-status', friend.online ? presenceLabelT(friend.status, heroName) : t('Desconectado')));
    const actions = h('div', 'friend-actions');
    const theirRoom = friend.online ? friend.status?.roomId : undefined;
    if (theirRoom && theirRoom !== myRoom?.roomId) {
      actions.append(button(t('Unirse'), 'friends-btn primary', () => this.join(theirRoom)));
    }
    if (friend.online && myRoom && theirRoom !== myRoom.roomId) {
      actions.append(button(t('Invitar'), 'friends-btn', () => social.send({ type: 'invite', userId: friend.id, roomId: myRoom.roomId, mode: myRoom.mode })));
    }
    if (friend.online || this.dms.has(friend.id)) {
      const message = button(t('Mensaje'), 'friends-btn', () => this.openChat(friend.id));
      const unread = this.unread.get(friend.id) ?? 0;
      if (unread) message.append(h('span', 'badge', String(unread)));
      actions.append(message);
    }
    actions.append(
      button('✕', 'friends-btn icon', () => {
        if (window.confirm(t('¿Eliminar a {name} de tus amigos?', { name: friend.username }))) social.send({ type: 'remove', userId: friend.id });
      }),
    );
    (actions.lastElementChild as HTMLElement).title = t('Eliminar amigo');
    row.append(dot, info, actions);
    return row;
  }

  private renderChat(friend: FriendEntry): void {
    const head = h('div', 'dm-head');
    head.append(
      button(t('← Volver'), 'link-btn', () => {
        this.chatWith = null;
        this.renderPanel();
      }),
    );
    const who = h('div', 'friend-info');
    who.append(h('span', 'friend-name', friend.username), h('span', 'friend-status', friend.online ? presenceLabelT(friend.status, heroName) : t('Desconectado')));
    head.append(who);

    const log = h('div', 'dm-log');
    const me = this.deps.social.state?.me.id;
    const messages = this.dms.get(friend.id) ?? [];
    if (!messages.length) log.append(h('p', 'friends-empty', t('Los mensajes no se guardan: sólo los ve tu amigo si está en línea.')));
    for (const message of messages) {
      const line = h('div', `dm-line${message.from.id === me ? ' mine' : ''}`);
      const time = new Date(message.at).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
      line.append(h('span', 'dm-text', message.text), h('span', 'dm-time', time));
      log.append(line);
    }

    const form = h('form', 'dm-form');
    const input = h('input');
    input.placeholder = friend.online ? t('Mensaje para {name}', { name: friend.username }) : t('{name} no está en línea', { name: friend.username });
    input.maxLength = 140;
    input.disabled = !friend.online;
    input.autocomplete = 'off';
    isolateKeys(input, () => this.closePanel());
    const send = h('button', 'friends-btn primary', t('ENVIAR'));
    send.type = 'submit';
    send.disabled = !friend.online;
    form.append(input, send);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const text = input.value.trim();
      if (!text) return;
      this.deps.social.send({ type: 'dm', userId: friend.id, text });
      input.value = '';
    });
    this.panelBody.replaceChildren(head, log, form);
    log.scrollTop = log.scrollHeight;
  }

  // ------------------------------------------------------------------ avisos

  private toast(kind: 'notice' | 'error' | 'invite' | 'dm', text: string, ms: number): HTMLElement {
    const toast = h('div', `social-toast ${kind}`);
    toast.append(h('span', 'toast-text', text));
    this.toasts.append(toast);
    while (this.toasts.children.length > 4) this.toasts.firstElementChild?.remove();
    window.setTimeout(() => toast.remove(), ms);
    return toast;
  }
}
